import { Button, Card, Modal, useToast } from '@citizen-walk/shared-ui';
import {
  formatMembershipProfileValue,
  membershipFieldStorageTarget,
  SESSIONS_FIELD_KEY,
  toStoredMembershipProfileValue,
} from '@citizen-walk/shared-types';
import type { FieldDef } from '@citizen-walk/shared-types';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import { useSessions } from '../sessions-admin';
import { DistrictParticipantAdminPanel } from './DistrictParticipantAdminPanel';
import { filterPersonRows, toPersonRows, type ParticipantPersonRow } from './participantRows';
import { useParticipantsAdmin } from './useParticipantsAdmin';

/**
 * ユーザー管理ページ (地区 > ユーザー管理、2026-09-07)。
 *   route: /districts/:districtId/participants
 *
 * この地区に参加している人の登録データ (ニックネーム / 属性 / 参加セッション) を確認し、
 * 窓口で聞き取った修正を管理者が直せるようにする。参加者の削除は行わない (投稿の持ち主が
 * 消えてしまうため。不要な参加者は運用で放置する)。
 *
 * 表示は必ず **人単位**: 端末の引き継ぎ・復帰をした参加者は登録データが端末の数だけあるので、
 * 端末リンクでまとめて 1 行にする (`toPersonRows`)。修正は同一人物の全端末に反映される。
 *
 * 入力欄は地区の「入力項目」設定 (dataSettings.userFields) から組み立てるので、モードや地区ごとの
 * 追加質問がそのまま編集できる (画面側に項目をハードコードしない)。
 *
 * 権限: マスター管理者は常に利用可。地区管理者は master が
 * 「地区管理者にもユーザー管理を許可する」を ON にした地区でのみ利用できる (サーバ側でも同じ判定)。
 */

/** ISO8601 → 「2026/09/05 14:30」。未設定は空文字。 */
function formatDateTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** 編集フォームの下書き (userFields 駆動のため key → 値の素朴な map で持つ)。 */
interface EditDraft {
  /** 専用欄。ニックネームは文字列、他は項目の型に応じた値 (複数選択は配列 等)。 */
  builtin: Record<string, unknown>;
  custom: Record<string, unknown>;
  joinedSessionIds: string[];
  mainSessionId: string;
}

function draftFromRow(row: ParticipantPersonRow): EditDraft {
  return {
    builtin: {
      nickname: row.nickname ?? '',
      gender: row.gender ?? '',
      ageGroup: row.ageGroup ?? '',
      relationship: row.relationship ?? '',
      attribute: row.attribute ?? '',
    },
    custom: { ...row.profileFieldValues },
    joinedSessionIds: [...row.joinedSessionIds],
    mainSessionId: row.mainSessionId ?? '',
  };
}

export function ParticipantsAdminPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const { state: authState } = useAdminAuth();
  const districtState = useDistrict(districtId ?? null);
  const sessionsState = useSessions(districtId ?? null);
  const { state, load, update, issueRecoveryCode, saving } = useParticipantsAdmin();
  const { toast } = useToast();

  const isMaster = authState.role === 'master';
  const district = districtState.status === 'ready' ? districtState.district : null;
  const canManage = isMaster || district?.participantAdmin?.districtAdminCanManage === true;

  const [keyword, setKeyword] = useState('');
  const [sessionFilter, setSessionFilter] = useState('');
  const [editing, setEditing] = useState<ParticipantPersonRow | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [issuedCode, setIssuedCode] = useState<{ nickname: string; code: string } | null>(null);
  const [confirmReissue, setConfirmReissue] = useState<ParticipantPersonRow | null>(null);

  useEffect(() => {
    if (districtId) void load(districtId);
  }, [districtId, load]);

  const sessions = useMemo(
    () => (sessionsState.status === 'ready' ? sessionsState.items : []),
    [sessionsState],
  );
  const sessionNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const s of sessions) m[s.id] = s.name;
    return m;
  }, [sessions]);

  const people = useMemo(
    () => toPersonRows(state.status === 'ready' ? state.participants : []),
    [state],
  );
  const filtered = useMemo(
    () => filterPersonRows(people, keyword, sessionFilter),
    [people, keyword, sessionFilter],
  );

  /** 編集フォームに出す項目 (地区の入力項目設定から。非表示の項目は出さない)。 */
  const userFields: FieldDef[] = useMemo(() => {
    const fields = district?.dataSettings?.userFields ?? [];
    return fields.filter((f) => f.mode !== 'hidden' && f.visible !== false);
  }, [district]);

  function openEdit(row: ParticipantPersonRow) {
    setEditing(row);
    setDraft(draftFromRow(row));
  }

  async function handleSave() {
    if (!districtId || !editing || !draft) return;
    const patch: Record<string, unknown> = {};
    for (const field of userFields) {
      const target = membershipFieldStorageTarget(field);
      if (target.kind === 'sessions') continue;
      if (target.kind === 'builtin') {
        const next = draft.builtin[target.column];
        patch[target.column] =
          target.column === 'nickname'
            ? typeof next === 'string'
              ? next
              : ''
            : toStoredMembershipProfileValue(next);
      } else {
        patch[`__custom__${field.key}`] = draft.custom[field.key] ?? null;
      }
    }
    const customEntries = Object.entries(patch)
      .filter(([k]) => k.startsWith('__custom__'))
      .map(([k, v]) => [k.replace('__custom__', ''), v] as const);
    for (const [k] of customEntries) delete patch[`__custom__${k}`];
    if (customEntries.length > 0) {
      patch.profileFieldValues = Object.fromEntries(customEntries);
    }
    patch.joinedSessionIds = draft.joinedSessionIds;
    patch.mainSessionId = draft.mainSessionId === '' ? null : draft.mainSessionId;

    try {
      const res = await update({
        districtId,
        uid: editing.uid,
        patch: patch as never,
      });
      toast(
        'success',
        res.updatedUids.length > 1
          ? `登録データを保存しました（この方の端末 ${res.updatedUids.length} 台に反映）`
          : '登録データを保存しました',
      );
      setEditing(null);
      setDraft(null);
      await load(districtId);
    } catch (e) {
      toast('error', `保存できませんでした: ${(e as Error).message}`);
    }
  }

  async function handleReissue(row: ParticipantPersonRow) {
    if (!districtId) return;
    try {
      const res = await issueRecoveryCode({ districtId, uid: row.uid });
      setConfirmReissue(null);
      setIssuedCode({ nickname: row.nickname || '(名前未設定)', code: res.recoveryCode });
      await load(districtId);
    } catch (e) {
      toast('error', `再発行できませんでした: ${(e as Error).message}`);
    }
  }

  if (!districtId) return null;

  return (
    <section className="grid grid-cols-1 gap-4">
      <div>
        <h1 className="text-lg font-bold text-slate-900">ユーザー管理</h1>
        <p className="text-sm text-slate-600 mt-1">
          この地区に参加している方の登録データを確認・修正します。
          <Link to={`/districts/${districtId}`} className="ml-2 text-primary-700 hover:underline">
            地区の概要へ戻る
          </Link>
        </p>
      </div>

      {isMaster && district && (
        <DistrictParticipantAdminPanel
          district={district}
          uid={authState.user?.uid ?? ''}
          canEdit={isMaster}
        />
      )}

      {!canManage ? (
        <Card padding="lg">
          <p className="text-sm text-slate-700">
            この地区のユーザー管理は、マスター管理者のみが利用できる設定になっています。
            利用したい場合はマスター管理者にご相談ください。
          </p>
        </Card>
      ) : (
        <>
          <Card padding="md">
            <div className="flex flex-wrap items-end gap-3">
              <label className="grid grid-cols-1 gap-1 min-w-0 flex-1">
                <span className="text-xs text-slate-500">名前・ユーザーIDで探す</span>
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="ニックネームの一部を入力"
                  className="min-h-touch px-3 border border-slate-300 rounded text-sm w-full"
                />
              </label>
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">参加セッションで絞り込む</span>
                <select
                  value={sessionFilter}
                  onChange={(e) => setSessionFilter(e.target.value)}
                  className="min-h-touch px-3 border border-slate-300 rounded text-sm"
                  style={{ maxWidth: '100%' }}
                >
                  <option value="">すべて</option>
                  {sessions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                  <option value="__none__">セッション未参加の方</option>
                </select>
              </label>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void load(districtId)}
                disabled={state.status === 'loading'}
              >
                最新の状態にする
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {state.status === 'ready' && (
                <>
                  {filtered.length} 名を表示 / この地区の参加者は {people.length} 名
                  {people.length < state.participants.length && (
                    <>（端末の引き継ぎ・復帰をした方は 1 名にまとめています）</>
                  )}
                </>
              )}
              {state.status === 'loading' && <>読込中…</>}
            </p>
          </Card>

          {state.status === 'error' && (
            <Card padding="md" className="border-danger-600">
              <p className="text-sm text-danger-700">読込エラー: {state.error.message}</p>
            </Card>
          )}

          {state.status === 'ready' && filtered.length === 0 && (
            <Card padding="md">
              <p className="text-sm text-slate-600">該当する方がいません。</p>
            </Card>
          )}

          {filtered.length > 0 && (
            <Card padding="sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-slate-50">
                    <tr className="text-left">
                      <th className="p-2 border-b border-slate-200">ニックネーム</th>
                      <th className="p-2 border-b border-slate-200">参加セッション</th>
                      <th className="p-2 border-b border-slate-200">属性</th>
                      <th className="p-2 border-b border-slate-200">最終アクセス</th>
                      <th className="p-2 border-b border-slate-200">引き継ぎコード</th>
                      <th className="p-2 border-b border-slate-200 text-right">操作</th>
                    </tr>
                  </thead>
                  <tbody data-testid="participants-table">
                    {filtered.map((row) => (
                      <tr key={row.uid} className="align-top">
                        <td className="p-2 border-b border-slate-100">
                          <p className="font-medium text-slate-800">
                            {row.nickname || '(名前未設定)'}
                          </p>
                          <p className="text-[11px] text-slate-500">
                            ユーザーID: <code>{row.uid}</code>
                            {row.deviceCount > 1 && <> ・ 端末 {row.deviceCount} 台</>}
                          </p>
                        </td>
                        <td className="p-2 border-b border-slate-100 text-slate-700">
                          {row.joinedSessionIds.length === 0
                            ? '—'
                            : row.joinedSessionIds
                                .map((sid) => sessionNameById[sid] ?? '(削除されたセッション)')
                                .join(' / ')}
                          {row.mainSessionId && (
                            <span className="block text-[11px] text-slate-500">
                              現在の対象: {sessionNameById[row.mainSessionId] ?? '—'}
                            </span>
                          )}
                        </td>
                        <td className="p-2 border-b border-slate-100 text-slate-700">
                          {[row.gender, row.ageGroup, row.relationship, row.attribute]
                            .map((v) => formatMembershipProfileValue(v))
                            .filter((v) => v !== '')
                            .join(' / ') || '—'}
                        </td>
                        <td className="p-2 border-b border-slate-100 text-slate-600 whitespace-nowrap">
                          {formatDateTime(row.lastSeenAt) || '—'}
                        </td>
                        <td className="p-2 border-b border-slate-100 text-slate-600">
                          {row.hasRecoveryCode ? '設定済み' : '未設定'}
                        </td>
                        <td className="p-2 border-b border-slate-100 text-right whitespace-nowrap">
                          <Button size="sm" variant="secondary" onClick={() => openEdit(row)}>
                            編集
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            className="ml-1"
                            onClick={() => setConfirmReissue(row)}
                          >
                            コード再発行
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      <Modal
        open={!!editing}
        onClose={() => {
          setEditing(null);
          setDraft(null);
        }}
        title="参加者の登録データを編集"
        size="lg"
        closeOnOverlayClick={false}
      >
        {editing && draft && (
          <div className="grid grid-cols-1 gap-3">
            {editing.deviceCount > 1 && (
              <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
                この方は端末の引き継ぎ・復帰をしています（端末 {editing.deviceCount}
                台）。変更はすべての端末に反映されます。
              </p>
            )}
            {userFields.map((field) => {
              const target = membershipFieldStorageTarget(field);
              if (field.key === SESSIONS_FIELD_KEY || target.kind === 'sessions') return null;
              const isBuiltin = target.kind === 'builtin';
              const value: unknown = isBuiltin
                ? draft.builtin[target.column]
                : draft.custom[field.key];
              // 項目の型のまま下書きに入れる (複数選択は配列、数値は数値、チェックは真偽値)。
              const setValue = (next: unknown) =>
                setDraft((d) =>
                  !d
                    ? d
                    : isBuiltin
                      ? { ...d, builtin: { ...d.builtin, [target.column]: next } }
                      : { ...d, custom: { ...d.custom, [field.key]: next } },
                );
              const text = typeof value === 'string' ? value : formatMembershipProfileValue(value);
              // 単一選択から複数選択に変えた項目の旧回答 (文字列 1 つ) も 1 件の選択として扱う。
              const selected = Array.isArray(value)
                ? value.filter((x): x is string => typeof x === 'string')
                : typeof value === 'string' && value !== ''
                  ? [value]
                  : [];
              return (
                <div key={field.key} className="grid grid-cols-1 gap-1">
                  <span className="text-xs text-slate-600" id={`pe-${field.key}`}>
                    {field.label}
                    {field.mode === 'required' && <span className="text-danger-700"> *</span>}
                  </span>
                  {field.type === 'multiselect' && field.options.length > 0 ? (
                    <span
                      role="group"
                      aria-labelledby={`pe-${field.key}`}
                      className="grid grid-cols-1 gap-1 border border-slate-200 rounded p-2"
                    >
                      {field.options.map((o) => (
                        <label key={o.value} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={selected.includes(o.value)}
                            onChange={(e) =>
                              setValue(
                                e.target.checked
                                  ? [...selected, o.value]
                                  : selected.filter((x) => x !== o.value),
                              )
                            }
                          />
                          {o.label || o.value}
                        </label>
                      ))}
                    </span>
                  ) : field.options.length > 0 ? (
                    <select
                      aria-labelledby={`pe-${field.key}`}
                      value={typeof value === 'string' ? value : ''}
                      onChange={(e) => setValue(e.target.value)}
                      className="min-h-touch px-3 border border-slate-300 rounded text-sm"
                      style={{ maxWidth: '100%' }}
                    >
                      <option value="">（未設定）</option>
                      {field.options.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label || o.value}
                        </option>
                      ))}
                    </select>
                  ) : field.type === 'checkbox' ? (
                    <input
                      type="checkbox"
                      aria-labelledby={`pe-${field.key}`}
                      checked={value === true}
                      onChange={(e) => setValue(e.target.checked)}
                      className="w-5 h-5"
                    />
                  ) : field.type === 'number' || field.type === 'rating-scale' ? (
                    <input
                      type="number"
                      aria-labelledby={`pe-${field.key}`}
                      value={typeof value === 'number' ? value : ''}
                      onChange={(e) =>
                        setValue(e.target.value === '' ? null : Number(e.target.value))
                      }
                      className="min-h-touch px-3 border border-slate-300 rounded text-sm"
                    />
                  ) : (
                    <input
                      type="text"
                      aria-labelledby={`pe-${field.key}`}
                      value={text}
                      onChange={(e) => setValue(e.target.value)}
                      className="min-h-touch px-3 border border-slate-300 rounded text-sm"
                    />
                  )}
                  {field.description && (
                    <span className="text-[11px] text-slate-500">{field.description}</span>
                  )}
                </div>
              );
            })}

            <fieldset className="border border-slate-200 rounded p-3">
              <legend className="text-xs text-slate-600 px-1">参加セッション</legend>
              {sessions.length === 0 ? (
                <p className="text-xs text-slate-500">この地区にはセッションがありません。</p>
              ) : (
                <div className="grid grid-cols-1 gap-1">
                  {sessions.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={draft.joinedSessionIds.includes(s.id)}
                        onChange={(e) =>
                          setDraft((d) => {
                            if (!d) return d;
                            const next = e.target.checked
                              ? [...d.joinedSessionIds, s.id]
                              : d.joinedSessionIds.filter((x) => x !== s.id);
                            return {
                              ...d,
                              joinedSessionIds: next,
                              // 参加から外したセッションが「現在の対象」だったら未設定に戻す。
                              mainSessionId: next.includes(d.mainSessionId) ? d.mainSessionId : '',
                            };
                          })
                        }
                      />
                      <span>{s.name}</span>
                    </label>
                  ))}
                </div>
              )}
              <label className="grid grid-cols-1 gap-1 mt-3">
                <span className="text-xs text-slate-600">
                  現在の対象セッション（投稿がどのセッションのものになるか）
                </span>
                <select
                  value={draft.mainSessionId}
                  onChange={(e) =>
                    setDraft((d) => (d ? { ...d, mainSessionId: e.target.value } : d))
                  }
                  className="min-h-touch px-3 border border-slate-300 rounded text-sm"
                  style={{ maxWidth: '100%' }}
                >
                  <option value="">（自動：参加セッションから決める）</option>
                  {sessions
                    .filter((s) => draft.joinedSessionIds.includes(s.id))
                    .map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                </select>
              </label>
            </fieldset>

            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setEditing(null);
                  setDraft(null);
                }}
                disabled={saving}
              >
                キャンセル
              </Button>
              <Button onClick={() => void handleSave()} disabled={saving}>
                {saving ? '保存中…' : '保存'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={!!confirmReissue}
        onClose={() => setConfirmReissue(null)}
        title="引き継ぎコードを再発行しますか？"
        size="md"
      >
        {confirmReissue && (
          <div className="grid grid-cols-1 gap-3">
            <p className="text-sm text-slate-700">
              「{confirmReissue.nickname || '(名前未設定)'}
              」さんの引き継ぎコードを新しく発行します。
              <strong className="block mt-1">今までのコードはこの地区では使えなくなります。</strong>
              新しいコードはこのあと 1 回だけ表示されます。本人に必ず控えてもらってください。
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirmReissue(null)} disabled={saving}>
                やめる
              </Button>
              <Button onClick={() => void handleReissue(confirmReissue)} disabled={saving}>
                {saving ? '発行中…' : '再発行する'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={!!issuedCode}
        onClose={() => setIssuedCode(null)}
        title="新しい引き継ぎコード"
        size="md"
        closeOnOverlayClick={false}
      >
        {issuedCode && (
          <div className="grid grid-cols-1 gap-3">
            <p className="text-sm text-slate-700">
              「{issuedCode.nickname}」さんの新しい引き継ぎコードです。この画面を閉じると
              二度と表示できません。
            </p>
            <p
              className="text-2xl font-mono tracking-widest text-center bg-slate-50 border border-slate-200 rounded py-3"
              data-testid="issued-recovery-code"
            >
              {issuedCode.code}
            </p>
            <div className="flex justify-end">
              <Button onClick={() => setIssuedCode(null)}>控えたので閉じる</Button>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
}
